import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sliders, Ruler, Waves } from "lucide-react";

export function CalculatorCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const steps = [
    { icon: Sliders, title: "Configurez", desc: "Choisissez la forme de votre terrasse et positionnez votre piscine", color: "#8BC34A" },
    { icon: Ruler, title: "Choisissez", desc: "Sélectionnez votre lame parmi nos 3 finitions bambou REBO", color: "#C4993B" },
    { icon: Waves, title: "Estimez", desc: "Obtenez instantanément les quantités de lames, lambourdes et clips", color: "#29B6F6" },
  ];

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #F5F2EA 0%, #ede9df 100%)" }}
      data-testid="calculator-cta-section"
    >
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1v38h38V1H1z' fill='%23000' fill-opacity='1'/%3E%3C/svg%3E\")" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left: Text content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-[#8BC34A] mb-4">
              Outil gratuit
            </span>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-5"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Calculez votre terrasse en{" "}
              <span className="text-[#8BC34A]">quelques clics</span>
            </h2>
            <p className="text-sm md:text-base text-[#666] mb-8 max-w-md leading-relaxed">
              Notre calculateur interactif vous permet de configurer votre projet
              et d'obtenir une estimation précise des matériaux nécessaires.
              Simple, rapide et gratuit.
            </p>

            {/* Steps */}
            <div className="space-y-4 mb-8">
              {steps.map((step, i) => (
                <motion.div
                  key={step.title}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.2, delay: i * 0.05 }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step.color}18` }}
                  >
                    <step.icon className="w-5 h-5" style={{ color: step.color }} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#2D2D2D] text-sm">{step.title}</div>
                    <div className="text-xs text-[#888]">{step.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link
              to="/calculateur"
              className="inline-flex items-center gap-2 rounded-2xl px-7 py-3.5 font-bold text-sm tracking-wide text-white hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #8BC34A 0%, #689F38 100%)",
                boxShadow: "0 4px 20px rgba(139,195,74,0.35)",
              }}
              data-testid="calculator-cta-btn"
            >
              Essayer le calculateur
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Right: Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.2 }}
            className="relative"
          >
            <div
              className="rounded-3xl p-8 md:p-10 relative overflow-hidden"
              style={{
                background: "linear-gradient(145deg, #2D2D2D 0%, #1a1a1a 100%)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              }}
            >
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#8BC34A15" }}>
                    <Ruler className="w-6 h-6 text-[#8BC34A]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">24.0 m²</div>
                    <div className="text-xs text-[#888]">Surface terrasse</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Lames", value: "76", color: "#C4993B" },
                    { label: "Lamb.", value: "24", color: "#9E9E9E" },
                    { label: "Clips", value: "480", color: "#B0BEC5" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl p-3 text-center"
                      style={{ background: "rgba(255,255,255,0.04)" }}
                    >
                      <div className="text-xl font-bold text-white tabular-nums">{item.value}</div>
                      <div className="text-[10px] mt-0.5" style={{ color: item.color }}>{item.label}</div>
                    </div>
                  ))}
                </div>

                <p className="text-[11px] text-[#555] leading-relaxed">
                  Exemple : terrasse 6m {"×"} 4m en Tropic Miel avec 10% de marge pour les coupes.
                </p>
              </div>
            </div>

            {/* Floating badge */}
            <motion.div
              className="absolute -top-3 -right-3 md:-top-4 md:-right-4 rounded-2xl px-4 py-2 shadow-lg"
              style={{ background: "#8BC34A" }}
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <span className="text-xs font-bold text-white">Gratuit</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
